import { SpamPreviewTestStatus, type SpamPreview } from '../../types/spam-preview';
import { ClickLoadingIndicator, SpamTestResult } from '../../components';
import React, { useMemo } from 'react';

const SpamTestResultMemo = React.memo(SpamTestResult);

export interface SpamFilterProps {
  spamPreview: SpamPreview | null;
  testState: SpamPreviewTestStatus;
  loading: boolean;
}

export const SpamFilter: React.FC<SpamFilterProps> = React.memo(
  ({ spamPreview, testState, loading }: SpamFilterProps) => {
    const providers = useMemo(() => spamPreview?.results || [], [spamPreview]);

    const errorState =
      testState === SpamPreviewTestStatus.ABANDONED || testState === SpamPreviewTestStatus.ERROR
        ? testState
        : false;

    const transformToReadableText = (status: SpamPreviewTestStatus): string => {
      if (status === SpamPreviewTestStatus.UNKNOWN) return 'Awaiting Update...';
      if (status === SpamPreviewTestStatus.FETCHING) return 'Fetching...';
      if (status === SpamPreviewTestStatus.ABANDONED) return 'Completed';
      const text = SpamPreviewTestStatus[status];
      const transformedText = text.replace(/_/g, ' ');
      const words = transformedText.split(' ');
      const capitalizedWords = words.map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase(),
      );
      return capitalizedWords.join(' ');
    };

    const textStatus = transformToReadableText(testState);
    const previewStatusTitle = `Spam Test Status: ${textStatus ?? 'Awaiting Update...'}`;

    // Check if test is in progress (before SEED_TEST_SENT or in FETCHING state)
    const isTestInProgress =
      loading ||
      testState === SpamPreviewTestStatus.FETCHING ||
      (testState >= SpamPreviewTestStatus.INITIALIZING &&
        testState < SpamPreviewTestStatus.SEED_TEST_SENT);

    // Empty state: show "not found" only when not loading, not in progress, and no results
    if (
      !loading &&
      !isTestInProgress &&
      providers.length === 0 &&
      testState !== SpamPreviewTestStatus.PENDING
    ) {
      return (
        <div
          className="spam-filter-root spam-filter-initializing"
          aria-label={previewStatusTitle}
        >
          <ul className="mail-provider-list p-2 spam-filter-provider-list">
            <div className="spam-filter-empty-state">
              <div className="spam-filter-empty-inner">
                <div className="spam-filter-empty-title">Spam Preview not found</div>
              </div>
            </div>
          </ul>
        </div>
      );
    }

    const isInitializing = providers?.length === 0 && isTestInProgress;

    return (
      <div
        className={`spam-filter-root ${isInitializing ? 'spam-filter-initializing' : ''}`}
        aria-label={previewStatusTitle}
      >
        <ul className="mail-provider-list p-2 spam-filter-provider-list">
          {providers?.map((provider) => (
            <SpamTestResultMemo
              key={provider.applicationName}
              result={provider}
              errorState={errorState}
            />
          ))}

          {isInitializing && (
            <div className="spam-filter-empty-state">
              <div className="spam-filter-empty-inner">
                <div className="spam-filter-empty-icon-wrapper">
                  <ClickLoadingIndicator
                    isLoading={true}
                    type="thumb"
                    width={80}
                    height={80}
                  />
                </div>
                <div className="spam-filter-empty-title">Spam filter initializing...</div>
              </div>
            </div>
          )}
        </ul>
      </div>
    );
  },
);

SpamFilter.displayName = 'SpamFilter';
